// ─────────────────────────────────────────────────────────
// Einstellungen → Bausteine (Kalkulation) — Bibliothek
// wiederverwendbarer Angebots-Positionen
// ─────────────────────────────────────────────────────────
// Eigenes Modul, damit CompanyDashboard.js nicht weiter wächst (Muster
// settingsWageGroups.js). Nutzt /api/bausteine (org-scoped,
// bausteine:read/write). Eingefügt werden Bausteine im Angebots-Editor
// (React OfferForm → BausteinPicker).
// Sprache: alle sichtbaren Texte über t() — Keys in i18n/de.json + en.json.
import { icon, escapeHtml } from '../../utils/dom.js';
import { createBaustein, updateBaustein, deleteBaustein } from '../../core/org.js';
import { money } from '../../utils/format.ts';
import { t } from '../../i18n/index.js';

// ── Listen-Bereich ────────────────────────────────────────
export function renderBausteineSection(ctx) {
  const { s, can } = ctx;
  if (!can('bausteine:read')) return `<div class="cd-set-empty">${escapeHtml(t('common.noPermission'))}</div>`;
  if (s.bsLoading) return `<div class="cd-set-empty">${escapeHtml(t('common.loading'))}</div>`;

  const list = s.bausteine || [];
  const info = `
    <div class="cd-emp-info">${icon('info', 18)}<span>${escapeHtml(t('settings.bausteine.info'))}</span></div>`;

  let body;
  if (!list.length) {
    body = `<div class="cd-emp-empty">${escapeHtml(t('settings.bausteine.empty'))}</div>`;
  } else {
    const rows = list
      .map((b) => {
        const clickable = can('bausteine:write');
        const clickAttr = clickable ? ` data-action="bs-edit-open" data-id="${escapeHtml(b.id)}"` : '';
        return `
          <div class="cd-bs-trow${clickable ? ' cd-emp-trow-click' : ''}"${clickAttr}>
            <div class="cd-wtm-name">${icon('widgets', 18)}<span>${escapeHtml(b.title)}</span></div>
            <div class="cd-bs-unit">${escapeHtml(b.unit || '–')}</div>
            <div class="cd-bs-num">${escapeHtml(money(b.unitPrice))}</div>
          </div>`;
      })
      .join('');
    body = `
      <div class="cd-emp-table">
        <div class="cd-bs-thead">
          <span>${escapeHtml(t('settings.bausteine.colTitle'))}</span><span>${escapeHtml(t('settings.bausteine.colUnit'))}</span><span>${escapeHtml(t('settings.bausteine.colPrice'))}</span>
        </div>
        ${rows}
      </div>`;
  }

  return `
    <div class="cd-emp">
      ${info}
      <div class="cd-emp-card">
        <div class="cd-emp-card-head">${icon('widgets', 18)}<h3>${escapeHtml(t('settings.bausteine.title'))}</h3></div>
        ${body}
      </div>
    </div>`;
}

export function bausteineHeadButton(ctx) {
  if (!ctx.can('bausteine:write')) return '';
  return `<button class="cd-btn-primary" data-action="bs-create-open">${icon('add', 16)}${escapeHtml(t('settings.bausteine.create'))}</button>`;
}

// ── Modal (Erstellen / Bearbeiten) ────────────────────────
export function renderBausteinModal(ctx) {
  const { s } = ctx;
  const e = s.bsEdit;
  if (!e || !e.open) return '';
  const dis = e.saving ? 'disabled' : '';
  const isEdit = !!e.id;

  const footBtns = isEdit
    ? (e.confirmDelete
        ? `<div class="cd-wtm-confirm"><span>${escapeHtml(t('settings.bausteine.confirmDeleteQuestion'))}</span>
             <button class="cd-btn-ghost" data-action="bs-delete-cancel" ${dis}>${escapeHtml(t('common.cancel'))}</button>
             <button class="cd-btn-danger" data-action="bs-delete-confirm" ${dis}>${escapeHtml(e.saving ? t('common.deleting') : t('common.confirmDelete'))}</button></div>`
        : `<button class="cd-btn-danger-ghost" data-action="bs-delete" ${dis}>${icon('delete', 16)}${escapeHtml(t('settings.bausteine.delete'))}</button>
           <div class="cd-wtm-foot-spacer"></div>
           <button class="cd-btn-ghost" data-action="bs-close" ${dis}>${escapeHtml(t('common.cancel'))}</button>
           <button class="cd-btn-primary" data-action="bs-submit" ${dis}>${escapeHtml(e.saving ? t('common.saving') : t('common.save'))}</button>`)
    : `<div class="cd-wtm-foot-spacer"></div>
       <button class="cd-btn-ghost" data-action="bs-close" ${dis}>${escapeHtml(t('common.cancel'))}</button>
       <button class="cd-btn-primary" data-action="bs-submit" ${dis}>${escapeHtml(e.saving ? t('common.saving') : t('common.save'))}</button>`;

  return `
    <div class="cd-emp-modal-overlay" data-action="bs-close">
      <div class="cd-emp-modal" data-action="bs-noop">
        <div class="cd-emp-modal-head">
          <h2>${icon('widgets', 20)}${escapeHtml(isEdit ? t('settings.bausteine.editTitle') : t('settings.bausteine.create'))}</h2>
          <button class="cd-emp-modal-x" data-action="bs-close" title="${escapeHtml(t('common.close'))}">${icon('close', 20)}</button>
        </div>
        <div class="cd-emp-modal-body">
          ${e.error ? `<div class="cd-error">${icon('error', 18)}<span>${escapeHtml(e.error)}</span></div>` : ''}
          <label class="cd-field"><span>${escapeHtml(t('settings.bausteine.titleLabel'))}</span><input class="cd-input" id="bs-title" type="text" placeholder="${escapeHtml(t('settings.bausteine.titlePlaceholder'))}" value="${escapeHtml(e.title || '')}" ${dis}></label>
          <label class="cd-field"><span>${escapeHtml(t('settings.bausteine.unitLabel'))}</span><input class="cd-input" id="bs-unit" type="text" placeholder="${escapeHtml(t('settings.bausteine.unitPlaceholder'))}" value="${escapeHtml(e.unit || '')}" ${dis}></label>
          <label class="cd-field"><span>${escapeHtml(t('settings.bausteine.priceLabel'))}</span><input class="cd-input" id="bs-price" type="number" step="0.05" min="0" value="${Number(e.unitPrice) || 0}" ${dis}></label>
        </div>
        <div class="cd-emp-modal-foot">${footBtns}</div>
      </div>
    </div>`;
}

// ── Aktions-Handler (gibt true zurück, wenn behandelt) ────
export function handleBausteinAction(a, el, ctx) {
  const { s, draw, reload } = ctx;
  switch (a) {
    case 'bs-noop':
      return true;
    case 'bs-create-open':
      if (!ctx.can('bausteine:write')) return true;
      s.bsEdit = { open: true, id: null, title: '', unit: '', unitPrice: 0, saving: false, error: '', confirmDelete: false };
      draw();
      return true;
    case 'bs-edit-open': {
      if (!ctx.can('bausteine:write')) return true;
      const b = (s.bausteine || []).find((x) => x.id === el.dataset.id);
      if (!b) return true;
      s.bsEdit = { open: true, id: b.id, title: b.title, unit: b.unit || '', unitPrice: b.unitPrice, saving: false, error: '', confirmDelete: false };
      draw();
      return true;
    }
    case 'bs-close':
      s.bsEdit = null;
      draw();
      return true;
    case 'bs-submit': {
      const e = s.bsEdit;
      if (!e) return true;
      const title = (document.getElementById('bs-title')?.value || '').trim();
      if (!title) { e.error = t('settings.bausteine.titleRequired'); draw(); return true; }
      const payload = {
        title,
        unit: (document.getElementById('bs-unit')?.value || '').trim() || null,
        unitPrice: Math.max(0, parseFloat(document.getElementById('bs-price')?.value) || 0),
      };
      e.saving = true; e.error = ''; draw();
      const req = e.id ? updateBaustein(e.id, payload) : createBaustein(payload);
      req
        .then(() => { s.bsEdit = null; if (reload) reload(); else draw(); })
        .catch((err) => { if (s.bsEdit) { s.bsEdit.saving = false; s.bsEdit.error = err.message || t('common.saveFailed'); } draw(); });
      return true;
    }
    case 'bs-delete': {
      const e = s.bsEdit;
      if (!e) return true;
      e.confirmDelete = true; draw();
      return true;
    }
    case 'bs-delete-cancel': {
      const e = s.bsEdit;
      if (!e) return true;
      e.confirmDelete = false; draw();
      return true;
    }
    case 'bs-delete-confirm': {
      const e = s.bsEdit;
      if (!e || !e.id) return true;
      e.saving = true; e.error = ''; draw();
      deleteBaustein(e.id)
        .then(() => { s.bsEdit = null; if (reload) reload(); else draw(); })
        .catch((err) => { if (s.bsEdit) { s.bsEdit.saving = false; s.bsEdit.confirmDelete = false; s.bsEdit.error = err.message || t('common.deleteFailed'); } draw(); });
      return true;
    }
    default:
      return false;
  }
}
